import { FC, ReactNode, useMemo } from 'react';
import type { JSX } from 'react';
import styles from './index.module.scss';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

type MarkdownBlock =
  | { type: 'paragraph'; content: string }
  | { type: 'heading'; level: number; content: string }
  | { type: 'blockquote'; content: string }
  | { type: 'list'; ordered: boolean; items: Array<{ content: string; value?: number }> }
  | { type: 'code'; content: string }
  | { type: 'hr' };

const parseBlocks = (input: string): MarkdownBlock[] => {
  const lines = input.replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let index = 0;
  const isOrderedLine = (value: string) => /^\d+[.)）]\s+/.test(value.trim());
  const isUnorderedLine = (value: string) => /^[-*+]\s+/.test(value.trim());

  while (index < lines.length) {
    const line = lines[index] ?? '';
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      index += 1;
      continue;
    }

    if (trimmedLine.startsWith('```')) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] || '').trim().startsWith('```')) {
        codeLines.push(lines[index] || '');
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      blocks.push({ type: 'code', content: codeLines.join('\n') });
      continue;
    }

    if (/^(---|\*\*\*)$/.test(trimmedLine)) {
      blocks.push({ type: 'hr' });
      index += 1;
      continue;
    }

    if (/^#{1,6}\s+/.test(trimmedLine)) {
      const match = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
      if (match) {
        blocks.push({ type: 'heading', level: match[1]?.length ?? 1, content: match[2] ?? '' });
        index += 1;
        continue;
      }
    }

    if (/^>\s?/.test(trimmedLine)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^>\s?/.test((lines[index] || '').trim())) {
        const quoteLine = (lines[index] || '').replace(/^\s*>\s?/, '');
        quoteLines.push(quoteLine);
        index += 1;
      }
      blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
      continue;
    }

    if (/^([-*+]\s+|\d+[.)）]\s+)/.test(trimmedLine)) {
      const ordered = isOrderedLine(trimmedLine);
      const items: Array<{ content: string; value?: number }> = [];
      const matchesList = ordered ? isOrderedLine : isUnorderedLine;

      let pendingBlankLine = false;
      while (index < lines.length) {
        const currentLine = lines[index] ?? '';
        const currentTrimmed = currentLine.trim();

        if (!currentTrimmed) {
          pendingBlankLine = true;
          index += 1;
          continue;
        }

        if (pendingBlankLine && !matchesList(currentTrimmed) && !/^\s+/.test(currentLine)) {
          break;
        }

        if (currentTrimmed.startsWith('```') || /^(---|\*\*\*)$/.test(currentTrimmed) || /^#{1,6}\s+/.test(currentTrimmed) || /^>\s?/.test(currentTrimmed)) {
          break;
        }

        if (matchesList(currentTrimmed)) {
          pendingBlankLine = false;
          if (ordered) {
            const match = currentTrimmed.match(/^(\d+)[.)）]\s+/);
            const value = match?.[1] ? Number.parseInt(match[1], 10) : undefined;
            items.push({ content: currentLine.replace(/^\s*\d+[.)）]\s+/, ''), value });
          } else {
            items.push({ content: currentLine.replace(/^\s*[-*+]\s+/, '') });
          }
          index += 1;
          continue;
        }

        if (isOrderedLine(currentTrimmed) || isUnorderedLine(currentTrimmed)) {
          if (items.length > 0) {
            const lastItem = items[items.length - 1];
            if (!lastItem) break;
            lastItem.content = `${lastItem.content}\n${currentLine}`;
            index += 1;
            continue;
          }
          break;
        }

        if (items.length > 0) {
          const prefix = pendingBlankLine ? '\n' : '';
          const lastItem = items[items.length - 1];
          if (lastItem) {
            lastItem.content = `${lastItem.content}${prefix}${currentLine}`;
          }
        }
        pendingBlankLine = false;
        index += 1;
      }
      if (ordered && items.length > 0) {
        const hasExplicitNumbers = items.some((item) => item.value !== undefined && item.value !== 1);
        if (!hasExplicitNumbers) {
          items.forEach((item) => {
            item.value = undefined;
          });
        }
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    const paragraphLines: string[] = [line];
    index += 1;
    while (index < lines.length) {
      const nextLine = lines[index] || '';
      const nextTrimmed = nextLine.trim();
      if (!nextTrimmed) break;
      if (nextTrimmed.startsWith('```')) break;
      if (/^(---|\*\*\*)$/.test(nextTrimmed)) break;
      if (/^#{1,6}\s+/.test(nextTrimmed)) break;
      if (/^>\s?/.test(nextTrimmed)) break;
      if (/^([-*+]\s+|\d+[.)）]\s+)/.test(nextTrimmed)) break;
      paragraphLines.push(nextLine);
      index += 1;
    }
    blocks.push({ type: 'paragraph', content: paragraphLines.join('\n') });
  }

  return blocks;
};

const renderInline = (text: string, nextKey: () => string): ReactNode[] => {
  const segments = text.split(/(`[^`]+`)/g);
  const nodes: ReactNode[] = [];

  segments.forEach((segment) => {
    if (!segment) return;
    if (segment.startsWith('`') && segment.endsWith('`') && segment.length > 1) {
      nodes.push(
        <code key={nextKey()} className={styles.inlineCode}>
          {segment.slice(1, -1)}
        </code>
      );
      return;
    }

    const strongParts = segment.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
    strongParts.forEach((part) => {
      if (!part) return;
      if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
        const inner = part.slice(2, -2);
        nodes.push(<strong key={nextKey()}>{renderInline(inner, nextKey)}</strong>);
        return;
      }

      const emParts = part.split(/(\*[^*]+\*|_[^_]+_)/g);
      emParts.forEach((emPart) => {
        if (!emPart) return;
        if ((emPart.startsWith('*') && emPart.endsWith('*')) || (emPart.startsWith('_') && emPart.endsWith('_'))) {
          nodes.push(<em key={nextKey()}>{emPart.slice(1, -1)}</em>);
        } else {
          nodes.push(emPart);
        }
      });
    });
  });

  return nodes;
};

const renderInlineWithBreaks = (text: string, nextKey: () => string): ReactNode[] => {
  const lines = text.split('\n');
  const nodes: ReactNode[] = [];
  lines.forEach((line, lineIndex) => {
    nodes.push(...renderInline(line, nextKey));
    if (lineIndex < lines.length - 1) {
      nodes.push(<br key={nextKey()} />);
    }
  });
  return nodes;
};

const renderBlocks = (blocks: MarkdownBlock[], nextKey: () => string): ReactNode[] => {
  return blocks.map((block) => {
    switch (block.type) {
      case 'heading': {
        const HeadingTag = `h${Math.min(6, Math.max(1, block.level))}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag key={nextKey()} className={styles.heading}>
            {renderInline(block.content, nextKey)}
          </HeadingTag>
        );
      }
      case 'blockquote':
        return (
          <blockquote key={nextKey()} className={styles.blockquote}>
            {renderInlineWithBreaks(block.content, nextKey)}
          </blockquote>
        );
      case 'list': {
        const ListTag = block.ordered ? 'ol' : 'ul';
        const listClassName = block.ordered ? styles.orderedList : styles.unorderedList;
        return (
          <ListTag key={nextKey()} className={`${styles.list} ${listClassName}`}>
            {block.items.map((item) => {
              const normalizedItem = item.content.replace(/\n{2,}/g, '\n');
              const itemBlocks = parseBlocks(normalizedItem);
              const valueProps = block.ordered && item.value !== undefined ? { value: item.value } : undefined;
              return (
                <li key={nextKey()} className={styles.listItem} {...valueProps}>
                  <div className={styles.listItemContent}>{renderBlocks(itemBlocks, nextKey)}</div>
                </li>
              );
            })}
          </ListTag>
        );
      }
      case 'code':
        return (
          <pre key={nextKey()} className={styles.codeBlock}>
            <code>{block.content}</code>
          </pre>
        );
      case 'hr':
        return <hr key={nextKey()} className={styles.divider} />;
      default:
        return (
          <p key={nextKey()} className={styles.paragraph}>
            {renderInlineWithBreaks(block.content, nextKey)}
          </p>
        );
    }
  });
};

const MarkdownContent: FC<MarkdownContentProps> = ({ content, className }) => {
  const nodes = useMemo(() => {
    let keyIndex = 0;
    const nextKey = () => `md-${keyIndex++}`;
    return renderBlocks(parseBlocks(content), nextKey);
  }, [content]);

  return <div className={`${styles.markdownContent} ${className || ''}`}>{nodes}</div>;
};

export default MarkdownContent;
